import type { Coverage } from '@/services/coverages/types';
import { Button, Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  useBusinessActivities,
  useBusinessAssistances,
  useBusinessCoverages,
  useBusinessPlanResourcesByCode,
  useFireProtectionRatingTypes,
  useTheftProtectionRatingTypes,
} from '../../hooks/business';
import { useConstructionTypes } from '../../hooks/homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../../hooks/homeowner/use-insured-objects.hook';
import { EditBusinessAddressModal } from './add-business-address-modal.component';
import {
  type BusinessLocation,
  formatBusinessLocationSummary,
} from './business-location';

export const QUESTIONNAIRE_ITEMS = [
  'Indique os sistemas protecionais para incêndio.',
  'Local de risco encontra-se em piso superior?',
  'O imóvel está localizado dentro de shopping?',
  'Indique as alarmes protecionais contra roubo.',
];

type SelectOption = {
  value: string;
  label: string;
};

export function CardActionButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='rounded-lg p-2 text-alba-blue-500 transition hover:bg-alba-gray-200'
    >
      {children}
    </button>
  );
}

type BusinessLocationCardProps = {
  location: BusinessLocation;
  locationNumber: number;
  isOpen: boolean;
  /** Destaca o card com borda vermelha (ex.: endereço duplicado). */
  hasError?: boolean;
  canEdit: boolean;
  isCopying?: boolean;
  isDeleting?: boolean;
  onToggleLocation: () => void;
  // eslint-disable-next-line no-unused-vars
  onDeleteBusinessLocation?: (_riskPk: number) => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateBusinessLocation?: (_location: BusinessLocation) => void;
  // eslint-disable-next-line no-unused-vars
  onCopyBusinessLocation?: (_riskPk: number) => void;
  constructionTypeOptions?: SelectOption[];
  insuredObjectOptions?: SelectOption[];
  coveragesCatalog?: Coverage[];
  /**
   * ID do produto no InsureMO (do `QuoteParamsContext` no fluxo de cotação).
   * Quando não fornecido (ex.: tela de detalhes da apólice, fora do provider),
   * o lookup de planos de assistência é desligado — o nome do pacote vem
   * direto de `location.assistancePackage`.
   */
  productId?: string;
  /** Jornada de cotação em análise: permite valores e exibe aviso do fluxo normal. */
  skipCoverageValueValidation?: boolean;
};

export function BusinessLocationCard({
  location,
  locationNumber,
  isOpen,
  hasError = false,
  canEdit,
  isCopying = false,
  isDeleting = false,
  onToggleLocation,
  onDeleteBusinessLocation,
  onUpdateBusinessLocation,
  onCopyBusinessLocation,
  constructionTypeOptions = [],
  insuredObjectOptions = [],
  coveragesCatalog,
  productId,
  skipCoverageValueValidation = false,
}: BusinessLocationCardProps) {
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Quando o catálogo não é fornecido pelo container (ex.: step-calculation),
  // o card busca seu próprio catálogo usando o `activityType` da location.
  const { data: ownCatalog = [] } = useBusinessCoverages(
    coveragesCatalog ? undefined : location.activityType
  );
  const resolvedCatalog = coveragesCatalog ?? ownCatalog;

  const coverageItems = (location.coveragesPayload ?? [])
    .filter(c => c.value > 0 && !['ASSIST', 'CAPITA'].includes(c.code))
    .map(c => {
      const catalogEntry = resolvedCatalog?.find(cat => cat.code === c.code);
      return {
        name: c.displayName ?? catalogEntry?.name ?? c.code,
        value: c.value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
        required: catalogEntry?.required ?? false,
      };
    });
  const basicCoverages = coverageItems.filter(c => c.required);
  const optionalCoverages = coverageItems.filter(c => !c.required);

  const [assistanceDetailsOpen, setAssistanceDetailsOpen] = useState(false);
  const { data: constructionTypesData } = useConstructionTypes();
  const { data: insuredObjectTypesData } = useInsuredObjects();
  const { data: businessActivitiesData } = useBusinessActivities();
  const { data: fireProtectionRatingTypes } = useFireProtectionRatingTypes();
  const { data: theftProtectionRatingTypes } = useTheftProtectionRatingTypes();
  const { data: assistancePlans = [] } = useBusinessAssistances(productId);

  const normalizePlanName = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();

  const savedAssistanceCode = String(
    location.assistancePackageCode || ''
  ).trim();

  const matchedPlanByCode = assistancePlans.find(
    plan => String(plan.code || '').trim() === savedAssistanceCode
  );

  const matchedPlanByStoredName = !matchedPlanByCode
    ? assistancePlans.find(
      plan =>
        normalizePlanName(plan.name || '') ===
        normalizePlanName(location.assistancePackage || '')
    )
    : undefined;

  const fallbackPlan =
    !matchedPlanByCode && !matchedPlanByStoredName
      ? assistancePlans.find(
        plan => normalizePlanName(plan.name || '') === 'especial'
      ) ?? assistancePlans[0]
      : undefined;

  const resolvedPlan =
    matchedPlanByCode || matchedPlanByStoredName || fallbackPlan;

  const resolvedAssistancePackageName =
    resolvedPlan?.name?.trim() ||
    location.assistancePackage ||
    savedAssistanceCode;

  const { data: planResources = [] } = useBusinessPlanResourcesByCode(
    resolvedPlan?.code || savedAssistanceCode || undefined
  );

  const contractedAssistances = (location.benefitsPayload ?? [])
    .filter(benefit => {
      const normalized = String(benefit.code || '').trim().toUpperCase();
      if (!normalized) return false;
      if (normalized === 'CAPITA' || normalized.endsWith('CAP')) return false;
      return true;
    })
    .map(benefit => {
      const resource = planResources.find(
        r => String(r.code || '').trim() === String(benefit.code).trim()
      );
      return {
        code: benefit.code,
        name: resource?.name?.trim() || benefit.code,
      };
    });

  const resolveQuestionnaireDisplay = (
    questionIndex: number,
    answer: string | string[] | undefined
  ): string => {
    const QUESTION_KIND: ('fire' | 'yesno' | 'theft')[] = [
      'fire',
      'yesno',
      'yesno',
      'theft',
    ];
    const kind = QUESTION_KIND[questionIndex];

    const resolveCode = (code: string): string => {
      if (kind === 'yesno') {
        if (code === 'Y' || code === 'S') return 'Sim';
        if (code === 'N') return 'Não';
        return code;
      }
      const catalog =
        kind === 'fire'
          ? (fireProtectionRatingTypes ?? [])
          : (theftProtectionRatingTypes ?? []);
      const found = catalog.find(item => String(item.code) === code);
      return found?.description || code;
    };

    if (Array.isArray(answer)) {
      return answer.length > 0
        ? answer.map(resolveCode).join(', ')
        : '-';
    }
    return answer ? resolveCode(answer) : '-';
  };

  const resolvedActivityTypeLabel =
    (businessActivitiesData ?? []).find(
      activity => String(activity.code) === location.activityType
    )?.description ?? location.activityTypeLabel;

  const resolvedConstructionTypeLabel =
    (constructionTypesData ?? []).find(
      t => String(t.code) === location.constructionType
    )?.description ??
    constructionTypeOptions.find(o => o.value === location.constructionType)
      ?.label ??
    location.constructionTypeLabel;

  const resolvedInsuredObjectTypeLabel =
    (insuredObjectTypesData ?? []).find(
      t => String(t.code) === location.insuredObjectType
    )?.description ??
    insuredObjectOptions.find(o => o.value === location.insuredObjectType)
      ?.label ??
    location.insuredObjectTypeLabel;

  return (
    <div
      key={location.id}
      className={`rounded-xl border p-4 ${
        hasError ? 'border-alba-error-red-400' : 'border-alba-gray-400'
      }`}
    >
      <div className='relative flex flex-row items-start justify-between gap-2 sm:static'>
        <div className='min-w-0 space-y-1'>
          <p className='pr-35 text-xl font-bold text-alba-black-500 sm:pr-0'>
            {`Local ${String(locationNumber).padStart(2, '0')}.`}
          </p>
          <p className='text-sm font-bold text-alba-black-500'>
            {formatBusinessLocationSummary(location)}
          </p>
        </div>

        <div className='absolute right-0 top-0 flex shrink-0 items-center gap-1 sm:static'>
          {canEdit && location.riskPk && (
            <CardActionButton onClick={() => setCopyDialogOpen(true)}>
              <Copy className='h-4 w-4' strokeWidth={1.6} />
            </CardActionButton>
          )}
          {canEdit && (
            <>
              {onUpdateBusinessLocation && (
                <EditBusinessAddressModal
                  location={location}
                  onConfirm={onUpdateBusinessLocation}
                  constructionTypeOptions={constructionTypeOptions}
                  insuredObjectOptions={insuredObjectOptions}
                  skipCoverageValueValidation={skipCoverageValueValidation}
                />
              )}
              {location.riskPk && (
                <CardActionButton onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className='h-4 w-4' strokeWidth={1.6} />
                </CardActionButton>
              )}
            </>
          )}
          <CardActionButton onClick={onToggleLocation}>
            {isOpen ? (
              <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
            ) : (
              <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
            )}
          </CardActionButton>
        </div>
      </div>

      <Dialog.Root
        open={copyDialogOpen}
        onOpenChange={details => setCopyDialogOpen(details.open)}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='max-w-md'>
            <Dialog.Header>
              <Dialog.Title>Copiar local de risco</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text>
                Deseja copiar o local de risco &quot;Local{' '}
                {String(locationNumber).padStart(2, '0')}&quot;? Uma cópia
                idêntica será criada.
              </Text>
            </Dialog.Body>
            <Dialog.Footer className='flex justify-end gap-3'>
              <Dialog.CloseTrigger asChild>
                <Button variant='outline' disabled={isCopying}>
                  Cancelar
                </Button>
              </Dialog.CloseTrigger>
              <Button
                loading={isCopying}
                disabled={isCopying}
                onClick={() => {
                  if (location.riskPk && onCopyBusinessLocation) {
                    onCopyBusinessLocation(location.riskPk);
                    setCopyDialogOpen(false);
                  }
                }}
              >
                Confirmar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root
        open={deleteDialogOpen}
        onOpenChange={details => setDeleteDialogOpen(details.open)}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='max-w-md'>
            <Dialog.Header>
              <Dialog.Title>Remover local de risco</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text>
                Deseja remover o local de risco &quot;Local{' '}
                {String(locationNumber).padStart(2, '0')}&quot;? Esta ação não
                poderá ser desfeita.
              </Text>
            </Dialog.Body>
            <Dialog.Footer className='flex justify-end gap-3'>
              <Dialog.CloseTrigger asChild>
                <Button variant='outline' disabled={isDeleting}>
                  Cancelar
                </Button>
              </Dialog.CloseTrigger>
              <Button
                loading={isDeleting}
                disabled={isDeleting}
                onClick={() => {
                  if (location.riskPk && onDeleteBusinessLocation) {
                    onDeleteBusinessLocation(location.riskPk);
                    setDeleteDialogOpen(false);
                  }
                }}
              >
                Remover
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {isOpen && (
        <div className='space-y-6 pt-4 mt-4 border-t border-alba-gray-300'>
          <div className='space-y-4'>
            <h3 className='text-base font-medium text-alba-black-500'>
              Dados do Risco
            </h3>

            <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>CEP</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.cep}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Endereço</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.street}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Número</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.number}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Complemento</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.complement || '-'}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Bairro</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.neighborhood}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Cidade</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {`${location.city} - ${location.state}`}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Tipo de Atividade</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {resolvedActivityTypeLabel}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Tipo de Construção</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {resolvedConstructionTypeLabel}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Objeto Segurado</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {resolvedInsuredObjectTypeLabel}
                </p>
              </div>
              <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                <p className='text-xs text-alba-gray-600'>Valor em Risco</p>
                <p className='text-sm font-medium text-alba-black-500'>
                  {location.riskValue}
                </p>
              </div>
              {location.businessInterruptionValue && (
                <div className='rounded-xl bg-alba-gray-200 px-5 py-3'>
                  <p className='text-xs text-alba-gray-600'>
                    Lucros Cessantes/Despesas Fixas
                  </p>
                  <p className='text-sm font-medium text-alba-black-500'>
                    {location.businessInterruptionValue}
                  </p>
                </div>
              )}
            </div>

            <p className='text-sm text-alba-black-500'>
              Imóvel Tombado?{' '}
              <span className='font-bold'>
                {location.isLandmarked ? 'Sim' : 'Não'}
              </span>
            </p>
          </div>

          <div className='space-y-4'>
            <h3 className='text-base font-medium text-alba-black-500'>
              Coberturas
            </h3>
            <div className='space-y-4'>
              {basicCoverages.length > 0 && (
                <div className='rounded-xl border border-alba-gray-400 p-4'>
                  <div className='border-b border-alba-gray-300 pb-2'>
                    <p className='text-sm font-bold text-alba-black-500'>
                      Básicas
                    </p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    {basicCoverages.map(item => (
                      <div
                        key={item.name}
                        className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                      >
                        <span>{item.name}</span>
                        <span className='text-right'>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {optionalCoverages.length > 0 && (
                <div className='rounded-xl border border-alba-gray-400 p-4'>
                  <div className='border-b border-alba-gray-300 pb-2'>
                    <p className='text-sm font-bold text-alba-black-500'>
                      Opcionais
                    </p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    {optionalCoverages.map(item => (
                      <div
                        key={item.name}
                        className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                      >
                        <span>{item.name}</span>
                        <span className='text-right'>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {coverageItems.length === 0 && (
                <p className='text-sm text-alba-gray-500'>
                  Nenhuma cobertura selecionada.
                </p>
              )}
            </div>
          </div>

          <div className='space-y-4'>
            <h3 className='text-base font-medium text-alba-black-500'>
              Assistências/Sorteio
            </h3>
            <div className='rounded-xl border border-alba-gray-400'>
              <div className='flex items-start justify-between gap-4 px-4 py-3'>
                <p className='text-sm font-bold text-alba-black-500'>
                  {resolvedAssistancePackageName || 'Sem pacote selecionado'}
                </p>
              </div>
              <button
                type='button'
                onClick={() => setAssistanceDetailsOpen(prev => !prev)}
                className='flex w-full items-center justify-end gap-2 border-t border-alba-gray-300 px-4 py-2 text-xs text-alba-gray-600'
              >
                <span>Mais Detalhes</span>
                {assistanceDetailsOpen ? (
                  <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                ) : (
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                )}
              </button>
              {assistanceDetailsOpen && (
                <div className='space-y-2 border-t border-alba-gray-300 px-4 py-3'>
                  {contractedAssistances.length === 0 ? (
                    <p className='text-xs text-alba-gray-600'>
                      Sem assistências contratadas para este pacote.
                    </p>
                  ) : (
                    contractedAssistances.map(item => (
                      <div
                        key={item.code}
                        className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                      >
                        <span className='flex-1'>{item.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='space-y-4'>
            <h3 className='text-base font-medium text-alba-black-500'>
              Questionário
            </h3>
            <div className='space-y-3'>
              {QUESTIONNAIRE_ITEMS.map((question, questionIndex) => (
                <div
                  key={`${location.id}-${question}`}
                  className='grid grid-cols-1 gap-3 lg:grid-cols-[1fr_minmax(260px,380px)] lg:items-center'
                >
                  <p className='text-sm text-alba-black-500'>{question}</p>
                  <div className='rounded-xl bg-alba-gray-200 px-5 py-2'>
                    <span className='text-xs text-alba-gray-600'>
                      {resolveQuestionnaireDisplay(
                        questionIndex,
                        location.questionnaireAnswers[questionIndex]
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
